import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import NotificationTray from '../NotificationTray/NotificationTray';
import './NotificationPanel.scss';

export default function NotificationPanel({ open, onClose, anchorRef }) {
  const { notifications } = useNotifications();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [panelPosition, setPanelPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setPanelPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchorBox = anchorRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const scale = viewportWidth <= 767 ? 1 : 0.68;
      const panelWidth = Math.min(506, viewportWidth - 32) * scale;
      const left = Math.max(16, Math.min(anchorBox.right - panelWidth, viewportWidth - panelWidth - 16));
      const top = anchorBox.bottom + 7;

      setPanelPosition({ left, top });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [anchorRef, open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const visibleNotifications = useMemo(
    () => notifications.filter((notif) => !dismissedIds.includes(notif.id)),
    [dismissedIds, notifications]
  );

  const handleDismiss = (id) => {
    setDismissedIds((prev) => [...prev, id]);
  };

  const handleSelect = (notification) => {
    if (!notification.targetPath) return;

    onClose();
    navigate(notification.targetPath);
  };

  if (!open) return null;

  return (
    <div
      className="notif-panel"
      ref={panelRef}
      style={panelPosition ? {
        '--notif-panel-left': `${panelPosition.left}px`,
        '--notif-panel-top': `${panelPosition.top}px`,
      } : undefined}
    >
      <NotificationTray
        notifications={visibleNotifications}
        onClose={onClose}
        onDismiss={handleDismiss}
        onSelect={handleSelect}
        mode="panel"
        role="dialog"
        headingId="notification-panel-heading"
      />
    </div>
  );
}
